import { PropsWithChildren, useMemo } from 'react';

import {
  ApolloCache,
  ApolloClient,
  NormalizedCacheObject,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { LocalState } from '@apollo/client/local-state';
import merge from 'deepmerge';

import { getCache } from './cache';
import { createLink } from './link';

let apolloClient: ApolloClient;

const typeOf = (value: unknown) => (typeof value).toLowerCase();

const shallowCompare = (source: unknown, target: unknown) => {
  if (typeOf(source) !== typeOf(target)) {
    return false;
  }

  if (Array.isArray(source) && Array.isArray(target)) {
    return source.length === target.length;
  } else if (source instanceof Date && target instanceof Date) {
    return source.getTime() === target.getTime();
  } else if (
    typeof source === 'object' &&
    source !== null &&
    typeof target === 'object' &&
    target !== null
  ) {
    return Object.keys(source).every(
      (key) =>
        (source as Record<string, unknown>)[key] ===
        (target as Record<string, unknown>)[key]
    );
  }

  return source === target;
};

const createApolloClient = (apolloCache: ApolloCache): ApolloClient => {
  if (process.env.NEXT_PUBLIC_SST_STAGE !== 'production') {
    loadDevMessages();
    loadErrorMessages();
  }

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: createLink(),
    cache: apolloCache,
    // Requis par Apollo Client 4 pour les champs `@client` : ils sont résolus
    // par les `read` functions du cache (typePolicies), sans resolvers.
    localState: new LocalState(),
  });
};

const initializeApollo = (
  initialState?: NormalizedCacheObject
): ApolloClient => {
  let _apolloClient = apolloClient;

  if (!_apolloClient) {
    _apolloClient = createApolloClient(getCache());
  }

  if (initialState) {
    const existingCache = _apolloClient.extract() as NormalizedCacheObject;

    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !shallowCompare(d, s))
        ),
      ],
    });

    _apolloClient.cache.restore(data);
  }

  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  if (!apolloClient) {
    apolloClient = _apolloClient;
  }

  return _apolloClient;
};

export type ApolloServiceProps = {
  initialApolloState?: NormalizedCacheObject;
};

export const ApolloService = ({
  initialApolloState,
  children,
}: PropsWithChildren<ApolloServiceProps>) => {
  const client = useMemo(
    () => initializeApollo(initialApolloState),
    [initialApolloState]
  );

  if (!client) {
    return null;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
